/**
 * App shell — left sidebar, ⌘K palette, page transitions (§13.2, Phase 8).
 *
 * Desktop: a fixed sidebar with the wordmark, a search field that opens the
 * palette, the nav (a single active pill that glides between items), and a
 * footer holding sync status, the planner, theme and sign-out.
 *
 * Phone (§13.1: down to 380px): a slim top bar plus a bottom tab bar with the
 * four most-used pages and "More", which opens the palette — it lists every
 * page, so nothing is unreachable, and eight icons in 380px was unreadable.
 */

import { useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowLeftRight,
  Calculator,
  CalendarClock,
  Coins,
  CreditCard,
  FileText,
  Hammer,
  LayoutDashboard,
  Moon,
  MoreHorizontal,
  Search,
  Settings as SettingsIcon,
  Sun,
} from "lucide-react";
import { useId } from "react";
import { NavLink, useLocation, useOutlet } from "react-router-dom";

import { SignOutButton } from "@/components/AuthGate";
import { SyncPill } from "@/components/SyncPill";
import { Button } from "@/components/ui/primitives";
import { dashboardApi } from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { CommandProvider, modKey, useCommands } from "@/lib/commands";
import { useTheme } from "@/lib/useTheme";

type NavItem = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  end?: boolean;
  temporary?: boolean;
  badge?: "review";
};

const NAV: NavItem[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/cards", label: "Cards", icon: CreditCard },
  // Not in §13.2's original list. Earned its place: eight cards means eight
  // different close and due dates, and holding those in your head is the one
  // chore the rest of the app doesn't remove.
  { to: "/cycles", label: "Cycles", icon: CalendarClock },
  { to: "/transactions", label: "Transactions", icon: ArrowLeftRight },
  { to: "/cashback", label: "Cashback", icon: Coins },
  { to: "/statements", label: "Statements", icon: FileText, badge: "review" },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
  // Temporary: live build progress and the questions that block it. Comes out
  // when the app is stable — this line, the route, and two files (see
  // routes/Status.tsx). Set apart so it reads as scaffolding, not a feature.
  { to: "/status", label: "Status", icon: Hammer, temporary: true },
];

const MOBILE_NAV = NAV.filter((n) => ["/", "/cards", "/transactions", "/cashback"].includes(n.to));

export function AppShell() {
  return (
    <CommandProvider>
      <Shell />
    </CommandProvider>
  );
}

function Shell() {
  const location = useLocation();
  const outlet = useOutlet();
  const reduce = useReducedMotion();

  return (
    <div className="relative min-h-screen overflow-x-clip">
      <div className="ambient" aria-hidden />

      <Sidebar />
      <MobileTopBar />

      <div className="md:pl-[15.5rem]">
        <main className="mx-auto w-full max-w-[84rem] px-4 pb-28 pt-6 sm:px-6 md:px-10 md:pb-20 md:pt-10">
          {/* A route change fades the new page up. Enter-only: an exit
              animation would make every navigation wait on the old page. */}
          <motion.div
            key={location.pathname}
            initial={reduce ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            {outlet}
          </motion.div>
        </main>
      </div>

      <MobileTabBar />
    </div>
  );
}

// --- Desktop sidebar ---------------------------------------------------------

function Sidebar() {
  const { openPalette, openPlanner } = useCommands();
  const reviewCount = useReviewCount();

  return (
    <aside className="fixed inset-y-0 left-0 z-40 hidden w-[15.5rem] flex-col border-r border-hairline bg-base/70 px-3 pb-3 pt-5 backdrop-blur-xl md:flex">
      <Wordmark className="px-2.5" />

      <button
        type="button"
        onClick={openPalette}
        className="group mt-6 flex h-10 items-center gap-2.5 rounded-xl border border-hairline bg-panel/60 px-3 text-left text-[13px] text-faint transition-colors hover:border-hairline-strong hover:text-muted"
      >
        <Search className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="truncate">Search or jump…</span>
        <kbd className="ml-auto shrink-0 whitespace-nowrap rounded-md border border-hairline px-1.5 py-px font-mono text-[10.5px] text-muted">
          {modKey()} K
        </kbd>
      </button>

      <nav className="mt-6 flex flex-col gap-0.5" aria-label="Main">
        {NAV.map((item) => (
          <SideLink key={item.to} item={item} count={item.badge === "review" ? reviewCount : 0} />
        ))}
      </nav>

      <div className="mt-auto space-y-2">
        <button
          type="button"
          onClick={openPlanner}
          className="group flex w-full items-center gap-3 rounded-xl border border-brass/20 bg-gradient-to-b from-brass/[0.09] to-brass/[0.03] px-3 py-2.5 text-left transition-colors hover:border-brass/35"
          title="Which card should this purchase go on?"
        >
          <span className="grid h-7 w-7 place-items-center rounded-lg bg-brass/15 text-brass">
            <Calculator className="h-3.5 w-3.5" aria-hidden />
          </span>
          <span className="min-w-0">
            <span className="block text-[13px] font-medium text-ink">Plan a purchase</span>
            <span className="block text-[11.5px] text-faint">Which card earns most?</span>
          </span>
        </button>

        <div className="flex items-center gap-1.5">
          <div className="min-w-0 flex-1">
            <SyncPill />
          </div>
          <ThemeButton />
          <SignOutButton />
        </div>
      </div>
    </aside>
  );
}

function SideLink({ item, count }: { item: NavItem; count: number }) {
  const { to, label, icon: Icon, end, temporary } = item;
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "group relative flex h-10 items-center gap-3 rounded-xl px-3 text-sm transition-colors duration-200",
          temporary && "mt-3 before:absolute before:-top-2 before:inset-x-3 before:h-px before:bg-hairline",
          isActive ? "text-ink" : "text-muted hover:bg-raised/50 hover:text-ink",
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* One pill, shared by every item: framer moves it between them. */}
          {isActive && (
            <motion.span
              layoutId="nav-active"
              aria-hidden
              className="absolute inset-0 rounded-xl border border-hairline-strong/70 bg-raised shadow-lift"
              transition={{ type: "spring", stiffness: 420, damping: 34 }}
            >
              <span className="absolute -left-3 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-r-full bg-brass shadow-[0_0_12px_rgb(var(--brass))]" />
            </motion.span>
          )}
          <Icon
            className={cn(
              "relative h-4 w-4 shrink-0 transition-colors",
              isActive ? "text-brass" : "text-faint group-hover:text-muted",
            )}
          />
          <span className="relative">{label}</span>
          {count > 0 && (
            <span
              className="relative ml-auto rounded-md bg-warn/15 px-1.5 font-mono text-[10.5px] leading-5 text-warn"
              title={`${count} statement${count === 1 ? "" : "s"} need checking`}
            >
              {count}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

// --- Phone -------------------------------------------------------------------

function MobileTopBar() {
  const { openPalette } = useCommands();
  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-hairline bg-base/80 px-4 backdrop-blur-xl md:hidden">
      <Wordmark />
      <div className="ml-auto flex min-w-0 items-center gap-1">
        <div className="min-w-0 max-w-[9.5rem]">
          <SyncPill />
        </div>
        <Button variant="ghost" size="icon" onClick={openPalette} aria-label="Search and commands">
          <Search className="h-4 w-4" />
        </Button>
        <ThemeButton />
        <SignOutButton />
      </div>
    </header>
  );
}

function MobileTabBar() {
  const { openPalette } = useCommands();
  return (
    <nav
      aria-label="Main"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-hairline bg-base/85 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl md:hidden"
    >
      <div className="grid h-16 grid-cols-5">
        {MOBILE_NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              cn(
                "relative flex flex-col items-center justify-center gap-1 text-[10.5px] transition-colors",
                isActive ? "text-ink" : "text-faint",
              )
            }
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <motion.span
                    layoutId="tab-active"
                    aria-hidden
                    className="absolute top-0 h-0.5 w-8 rounded-b-full bg-brass"
                    transition={{ type: "spring", stiffness: 420, damping: 34 }}
                  />
                )}
                <Icon className={cn("h-[18px] w-[18px]", isActive && "text-brass")} />
                {label}
              </>
            )}
          </NavLink>
        ))}
        <button
          type="button"
          onClick={openPalette}
          className="flex flex-col items-center justify-center gap-1 text-[10.5px] text-faint"
        >
          <MoreHorizontal className="h-[18px] w-[18px]" />
          More
        </button>
      </div>
    </nav>
  );
}

// --- Pieces ------------------------------------------------------------------

function Wordmark({ className }: { className?: string }) {
  return (
    <NavLink to="/" className={cn("flex items-center gap-2.5", className)} aria-label="MyDeck home">
      <FanMark />
      <span className="font-display text-[17px] font-semibold tracking-[-0.03em] text-ink">MyDeck</span>
    </NavLink>
  );
}

/**
 * The fan — the app icon's mark (public/icons/icon.svg, owner's pick
 * 2026-11-04) without its tile: graphite, titanium, gold on top. Drawn with
 * the favicon's geometry, which was made to read at 16px. Its own gradient
 * ids per instance, because the sidebar and the phone top bar both draw one.
 */
function FanMark() {
  const id = useId().replace(/:/g, "");
  return (
    <svg aria-hidden viewBox="6 2 54 56" className="h-[22px] w-[21px] drop-shadow-[0_1px_2px_rgb(0_0_0/0.35)]">
      <defs>
        <linearGradient id={`${id}g`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#4a4a4f" />
          <stop offset="1" stopColor="#232326" />
        </linearGradient>
        <linearGradient id={`${id}t`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#f2f2f0" />
          <stop offset="0.5" stopColor="#c4c4c6" />
          <stop offset="1" stopColor="#7d7d82" />
        </linearGradient>
        <linearGradient id={`${id}a`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#f3dfb0" />
          <stop offset="0.55" stopColor="#e6c88a" />
          <stop offset="1" stopColor="#a88a52" />
        </linearGradient>
      </defs>
      <g transform="translate(33 54)">
        <rect x="-13.5" y="-47" width="27" height="42" rx="4.5" fill={`url(#${id}g)`} stroke="#5c5c62" strokeWidth="1" transform="rotate(-22)" />
        <rect x="-13.5" y="-47" width="27" height="42" rx="4.5" fill={`url(#${id}t)`} transform="rotate(-8)" />
        <rect x="-13.5" y="-47" width="27" height="42" rx="4.5" fill={`url(#${id}a)`} transform="rotate(12)" />
      </g>
    </svg>
  );
}

function ThemeButton() {
  const { theme, toggle } = useTheme();
  const next = theme === "dark" ? "light" : "dark";
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggle}
      aria-label={`Switch to ${next} theme`}
      title={`Switch to ${next} theme`}
      className="shrink-0"
    >
      {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </Button>
  );
}

/** Statements waiting for the user — the same figure the dashboard banner uses. */
function useReviewCount(): number {
  const { data } = useQuery({
    queryKey: ["dashboard"],
    queryFn: dashboardApi.summary,
    staleTime: 60_000,
  });
  return data?.needs_review ?? 0;
}
